/**
 * Feature-flag shim — reads VITE_* flags from `import.meta.env` at build time.
 *
 * Merged surface from Vulns V3 Slice 3 + V4 Slice 3. Kept generic so V5 / V6
 * can reuse without a second copy.
 *
 * Design notes:
 * - VITE_* envs are baked at `pnpm build` time (see `apps/api/Dockerfile`'s
 *   `ARG VITE_*` + `ENV VITE_*` declarations — missing one means the flag is
 *   permanently `undefined` in the bundle regardless of the DO App spec).
 * - `"true"` (case-insensitive, trimmed) is the only value that enables a
 *   flag. Everything else — including `undefined`, empty string, `"1"`,
 *   `"TRUE "` with stray whitespace — is coerced before comparison.
 * - V3 additionally supports a runtime override via `window.__VITE_FLAGS__`
 *   so Playwright tests can toggle the flag without rebuilding. The override
 *   is keyed on the same `VITE_*` name for consistency with the build-time
 *   variant. V4's chip helper uses plain `readFlag` for parity with its
 *   originally shipped signature.
 */

export function readFlag(name: string): boolean {
  const raw = (import.meta.env as Record<string, string | undefined>)[name]
  if (typeof raw !== 'string') return false
  return raw.trim().toLowerCase() === 'true'
}

/**
 * Read a flag with an optional runtime override. If `window.__VITE_FLAGS__[name]`
 * is set (test harness injection), its value wins; otherwise the build-time
 * `import.meta.env` value is used.
 */
function readFlagWithRuntimeOverride(name: string): boolean {
  if (typeof window !== 'undefined') {
    const runtime = (window as unknown as {
      __VITE_FLAGS__?: Record<string, string>
    }).__VITE_FLAGS__
    const override = runtime?.[name]
    if (typeof override === 'string') {
      return override.trim().toLowerCase() === 'true'
    }
  }
  return readFlag(name)
}

/** Vulns V3 CISO Remediation section on the vulnerability detail page. Default off. */
export function isV3RemediationEnabled(): boolean {
  return readFlagWithRuntimeOverride('VITE_VULNS_V3_REMEDIATION_ENABLED')
}

/** Vulns V4 EPSS chip on the CISO detail page. Default off. */
export function isV4EpssChipEnabled(): boolean {
  return readFlag('VITE_VULNS_V4_EPSS_CHIP_ENABLED')
}

/**
 * Vulns V5 vendor-advisory list inside the Remediation card + admin suppress
 * section on `VulnerabilityEditPage`. Default off. Uses the runtime override
 * so Playwright can toggle it per-test via `window.__VITE_FLAGS__`.
 */
export function isV5AdvisoriesEnabled(): boolean {
  return readFlagWithRuntimeOverride('VITE_VULNS_V5_ADVISORIES_ENABLED')
}

/**
 * Vulns V6 CIRCL enrichment cards (ActiveExploitation / AffectedProducts /
 * AttackAndWeaknesses) on the vulnerability detail page. Default off.
 * Uses the runtime override so Playwright can toggle it via
 * `window.__VITE_FLAGS__` without rebuilding.
 */
export function isV6EnrichmentEnabled(): boolean {
  return readFlagWithRuntimeOverride('VITE_VULNS_V6_ENRICHMENT_ENABLED')
}
